// A blueprint (RFC-055) as the /bank editor's fields hold it (byj-1wv.90):
// the keys an admin sets most, as fields, and every other key kept as the
// text it was written in, so a save from the fields changes nothing the
// fields do not show. The fields are read from a blueprint's EDN with
// edn.ts and written back as EDN; what byj reads of that EDN is pinned by the
// golden files under share/testdata/blueprints, read by both this client's
// tests and exam-bank's read-blueprint.
import { type Edn, EdnError, ednString, keywordName, readEdn } from "./edn";
import { type Competency, competencies } from "./competencies";

/** The difficulty bands of :difficulty. */
export const bands = ["easy", "medium", "hard"] as const;
/** The item families :require names (exam/item-families). */
export const families = ["analyse", "produce", "tidy", "trap", "guard", "knowledge"] as const;

export type Band = (typeof bands)[number];
export type Family = (typeof families)[number];

/** The fields, each as it is typed: a number as its text, "" when not given. */
export interface BlueprintFields {
  id: string;
  title: string;
  audience: string;
  minutes: string;
  items: string;
  forms: string;
  passMark: string;
  approval: boolean;
  coverage: Record<Competency, string>;
  difficulty: Record<Band, string>;
  require: Family[];
  knowledge: { items: string; weight: string; minutes: string };
}

/** The fields and, in the order written, each key they do not hold: its name (`:model`) and its text (`:model "sonnet"`). */
export interface BlueprintForm {
  fields: BlueprintFields;
  rest: { key: string; text: string }[];
}

/** The keys the fields hold, in the order a blueprint is written. */
export const fieldKeys = ["id", "title", "audience", "minutes", "items", "coverage", "difficulty", "require", "knowledge", "forms", "pass-mark", "approval"] as const;

export const blankFields = (): BlueprintFields => ({
  id: "",
  title: "",
  audience: "",
  minutes: "",
  items: "",
  forms: "",
  passMark: "",
  approval: false,
  coverage: { delegation: "", description: "", discernment: "", diligence: "" },
  difficulty: { easy: "", medium: "", hard: "" },
  require: [],
  knowledge: { items: "", weight: "", minutes: "" },
});

/**
 * Why the fields cannot hold a blueprint, for the editor to say in its own
 * words: text that does not read (where), not a map, a key written twice, a
 * key the fields know written in another shape, or one naming a value they
 * do not know (a competency, a band, a family).
 */
export type Cannot = { kind: "read"; at: number } | { kind: "notMap" } | { kind: "twice" | "shape" | "value"; key: string };

/** A value of the key being read in a shape the fields do not take ("shape"), or naming what they do not know ("value"). */
class Shape extends Error {
  constructor(readonly kind: "shape" | "value") {
    super(kind);
  }
}
const kwName = (v: Edn) => {
  if (v.t !== "kw") throw new Shape("shape");
  return v.name;
};
const str = (v: Edn) => {
  if (v.t !== "str") throw new Shape("shape");
  return v.value;
};
/** A number as the fields write one (4, 0.25): another spelling (4.0, +4, 40N, 1e2) is kept as text, not rewritten. */
const num = (v: Edn, whole = false) => {
  if (v.t !== "num" || !(whole ? wholeText : numberText).test(v.raw)) throw new Shape("shape");
  return v.raw;
};
/**
 * A map's entries as keyword → value, each key one of `allowed`, once. An
 * empty map, which the fields would write as no key, and a key written twice,
 * which Clojure's reader refuses and the fields would hold one value of, are
 * not theirs to hold.
 */
const kwMap = <K extends string>(v: Edn, allowed: readonly K[]): [K, Edn][] => {
  if (v.t !== "map" || v.entries.length === 0) throw new Shape("shape");
  const out = v.entries.map(({ key, value }): [K, Edn] => {
    const k = kwName(key);
    if (!(allowed as readonly string[]).includes(k)) throw new Shape("value");
    return [k as K, value];
  });
  if (new Set(out.map(([k]) => k)).size !== out.length) throw new Shape("shape");
  return out;
};

/**
 * The fields of a blueprint's EDN, or why the fields cannot hold it: text
 * that does not read, or a key the fields know written in a shape they do
 * not (the editor then stays on the text).
 */
export function fromEdn(text: string): { ok: true; form: BlueprintForm } | { ok: false; why: Cannot } {
  let top: Edn;
  try {
    top = readEdn(text);
  } catch (err) {
    if (err instanceof EdnError) return { ok: false, why: { kind: "read", at: err.at } };
    throw err;
  }
  if (top.t !== "map") return { ok: false, why: { kind: "notMap" } };
  const f = blankFields();
  const rest: BlueprintForm["rest"] = [];
  const seen = new Set<string>();
  // the key being read as written (:minutes, or "id" for one that is not a keyword), for why the fields cannot hold it
  let named = "";
  try {
    for (const { key, value } of top.entries) {
      named = text.slice(key.start, key.end);
      const k = kwName(key);
      if (seen.has(k)) return { ok: false, why: { kind: "twice", key: named } };
      seen.add(k);
      if (!(fieldKeys as readonly string[]).includes(k)) {
        rest.push({ key: named, text: text.slice(key.start, value.end) });
        continue;
      }
      switch (k) {
        case "id":
          f.id = str(value);
          break;
        case "title":
          f.title = str(value);
          break;
        case "audience":
          f.audience = kwName(value);
          break;
        case "minutes":
          f.minutes = num(value, true);
          break;
        case "items":
          f.items = num(value, true);
          break;
        case "forms":
          f.forms = num(value, true);
          break;
        case "pass-mark":
          f.passMark = num(value);
          break;
        case "approval":
          if (value.t !== "bool") throw new Shape("shape");
          f.approval = value.value;
          break;
        case "coverage":
          for (const [c, v] of kwMap(value, competencies)) f.coverage[c] = num(v);
          break;
        case "difficulty":
          for (const [b, v] of kwMap(value, bands)) f.difficulty[b] = num(v);
          break;
        case "require": {
          if (value.t !== "vec") throw new Shape("shape");
          for (const x of value.items) {
            const fam = kwName(x);
            if (!(families as readonly string[]).includes(fam)) throw new Shape("value");
            f.require.push(fam as Family);
          }
          // in families' order, the order the boxes and toEdn keep, so ticking one off and on again changes nothing
          f.require = families.filter((x) => f.require.includes(x));
          break;
        }
        case "knowledge":
          for (const [kk, v] of kwMap(value, ["items", "weight", "minutes"] as const)) f.knowledge[kk] = num(v, kk !== "weight");
          break;
      }
    }
  } catch (err) {
    if (err instanceof Shape) return { ok: false, why: { kind: err.kind, key: named } };
    throw err;
  }
  return { ok: true, form: { fields: f, rest } };
}

/** A number field's text as written: trimmed, nothing when empty. */
const given = (s: string) => s.trim();
/** The audience typed, its keyword's colon taken off when it was typed with one (the key list writes :non-developer). */
const audienceName = (s: string) => s.trim().replace(/^:/, "");
/** A map of keywords to the given numbers among `keys`, or nothing when none is given. */
const numberMap = <K extends string>(keys: readonly K[], values: Record<K, string>) => {
  const parts = keys.filter((k) => given(values[k]) !== "").map((k) => `:${k} ${given(values[k])}`);
  return parts.length ? `{${parts.join(" ")}}` : "";
};

/**
 * A blueprint's EDN from its fields and the keys they do not hold, a key a
 * line in the order a blueprint is written (the order of the editor's
 * example), each kept key's text after them as it was written. A field left
 * empty writes no key; problems() says which of those a blueprint needs.
 */
export function toEdn({ fields: f, rest }: BlueprintForm): string {
  const lines: string[] = [];
  const add = (k: string, v: string) => v !== "" && lines.push(`:${k} ${v}`);
  // as typed, spaces and all: a field left blank writes no key, and one changed changes nothing else
  add("id", f.id.trim() === "" ? "" : ednString(f.id));
  add("title", f.title.trim() === "" ? "" : ednString(f.title));
  add("audience", audienceName(f.audience) === "" ? "" : `:${audienceName(f.audience)}`);
  add("minutes", given(f.minutes));
  add("items", given(f.items));
  add("coverage", numberMap(competencies, f.coverage));
  add("difficulty", numberMap(bands, f.difficulty));
  add("require", f.require.length ? `[${families.filter((x) => f.require.includes(x)).map((x) => `:${x}`).join(" ")}]` : "");
  add("knowledge", numberMap(["items", "weight", "minutes"] as const, f.knowledge));
  add("forms", given(f.forms));
  add("pass-mark", given(f.passMark));
  add("approval", f.approval ? "true" : "");
  lines.push(...rest.map((r) => r.text));
  return `{${lines.join("\n ")}}`;
}

/** What is wrong with the fields as a blueprint byj reads (exam-bank's blueprint-schema), as the editor's words' keys. */
export type Problem =
  | "idMissing"
  | "titleMissing"
  | "audienceKeyword"
  | "minutesWhole"
  | "itemsWhole"
  | "itemsNone"
  | "formsWhole"
  | "passMark"
  | "coverageNumbers"
  | "coverageSum"
  | "difficultyNumbers"
  | "difficultyNone"
  | "knowledgeItems"
  | "knowledgeWeight"
  | "knowledgeMinutes";

// as Clojure's reader takes a number: no leading zero, which it refuses (08) or reads as octal (010)
const wholeText = /^(0|[1-9]\d*)$/;
const numberText = /^(0|[1-9]\d*)(\.\d+)?$/;
const whole = (s: string) => wholeText.test(given(s));
const number = (s: string) => numberText.test(given(s));
/** A whole number of at least one. */
const counted = (s: string) => whole(s) && Number(given(s)) >= 1;
/** The sum of the given numbers among `values`, rounded to a hundredth. */
export const shareSum = (values: string[]) => Math.round(values.filter((v) => number(v)).reduce((n, v) => n + Number(given(v)), 0) * 100) / 100;
/** Whether coverage shares add to 1, a hundredth either way allowed for shares rounded as written (0.33 three times). */
export const addsToOne = (sum: number) => Math.abs(sum - 1) < 0.015;

/** The problems of the fields, none when byj can draw from what they write. */
export function problems(f: BlueprintFields): Problem[] {
  const out: Problem[] = [];
  if (f.id.trim() === "") out.push("idMissing");
  if (f.title.trim() === "") out.push("titleMissing");
  if (audienceName(f.audience) !== "" && !keywordName(audienceName(f.audience))) out.push("audienceKeyword");
  if (!counted(f.minutes)) out.push("minutesWhole");
  const knowledge = given(f.knowledge.items) !== "" || given(f.knowledge.weight) !== "" || given(f.knowledge.minutes) !== "";
  if (!whole(f.items)) out.push("itemsWhole");
  // the server takes a blueprint that draws practical items or knowledge ones (examserver readBlueprint)
  else if (Number(given(f.items)) === 0 && !knowledge) out.push("itemsNone");
  if (given(f.forms) !== "" && !counted(f.forms)) out.push("formsWhole");
  if (given(f.passMark) !== "" && !(number(f.passMark) && Number(given(f.passMark)) <= 100)) out.push("passMark");
  // a competency left out is none of it, as byj reads :coverage; those given are numbers adding to 1,
  // since byj holds a paper to each competency's share (exam-bank coverage-error)
  const cov = competencies.map((c) => f.coverage[c]).filter((v) => given(v) !== "");
  if (cov.length === 0 || !cov.every(number)) out.push("coverageNumbers");
  else if (!addsToOne(shareSum(cov))) out.push("coverageSum");
  // byj takes each difficulty share as its part of their sum (exam-bank band-counts): any sum but none
  const diff = bands.map((b) => f.difficulty[b]).filter((v) => given(v) !== "");
  if (diff.length) {
    if (!diff.every(number)) out.push("difficultyNumbers");
    // unrounded: a share as small as 0.004 is still all of a sum
    else if (!diff.some((v) => Number(given(v)) > 0)) out.push("difficultyNone");
  }
  const k = f.knowledge;
  if (knowledge) {
    if (!counted(k.items)) out.push("knowledgeItems");
    if (!number(k.weight)) out.push("knowledgeWeight");
    if (given(k.minutes) !== "" && !whole(k.minutes)) out.push("knowledgeMinutes");
  }
  return out;
}
